import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const TOKEN_KEY = "nc_dash_token";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [status, setStatus] = useState("checking"); // checking | anon | authed

  const apply = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
      axios.defaults.headers.common.Authorization = `Bearer ${t}`;
    } else {
      localStorage.removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common.Authorization;
    }
    setToken(t || "");
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
    // 401 interceptor — clears token globally
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          apply("");
          setStatus("anon");
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [apply, token]);

  useEffect(() => {
    let cancelled = false;
    const verify = async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const { data } = await axios.get(`${base}/api/auth/check`);
        if (cancelled) return;
        setStatus(data.authenticated ? "authed" : "anon");
        if (!data.authenticated) apply("");
      } catch (e) {
        if (cancelled) return;
        setStatus("anon");
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [token, apply]);

  const login = async (password) => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const { data } = await axios.post(`${base}/api/auth/login`, { password });
    apply(data.token);
    setStatus("authed");
    return data;
  };

  const logout = () => {
    apply("");
    setStatus("anon");
  };

  return (
    <AuthContext.Provider value={{ token, status, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
